'use strict';

var jsxRuntime = require('react/jsx-runtime');
var cauldronReact = require('@deque/cauldron-react');
var React = require('react');
var reactI18next = require('react-i18next');
var i18n = require('i18next');
var utils = require('@react-aria/utils');
var billingUtils = require('@deque/billing-utils');
var removeTrailingSlash = require('remove-trailing-slash');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var React__default = /*#__PURE__*/_interopDefault(React);
var i18n__default = /*#__PURE__*/_interopDefault(i18n);
var removeTrailingSlash__default = /*#__PURE__*/_interopDefault(removeTrailingSlash);

const instance = i18n__default.default.createInstance({
    lng: 'en-US',
    fallbackLng: 'en-US',
    debug: process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test',
    ns: ['launchpad'],
    defaultNS: 'launchpad',
    interpolation: {
        escapeValue: false // react is already xss-safe
    },
    nsSeparator: false,
    keySeparator: false
});
instance.init();

function useTranslation(...args) {
    /* c8 ignore next 1 */
    const [ns = 'launchpad', options] = args;
    return reactI18next.useTranslation(ns, { i18n: instance, ...options });
}

const defaultApiConfig = {
    apiHostname: '',
    authFn: () => Promise.resolve(''),
    userEnterpriseId: '',
    hostProduct: {
        name: '',
        slug: ''
    }
};
const ApiConfigContext = React.createContext(defaultApiConfig);
const ApiConfigProvider = ({ apiHostname, authFn, userEnterpriseId, hostProduct, children }) => {
    const apiConfig = React.useMemo(() => {
        return {
            apiHostname,
            authFn,
            userEnterpriseId,
            hostProduct
        };
    }, [apiHostname, authFn, userEnterpriseId, hostProduct]);
    return (jsxRuntime.jsx(ApiConfigContext.Provider, { value: apiConfig, children: children }));
};
const useApiConfigContext = () => React.useContext(ApiConfigContext);

/**
 * Note: This method should not be called directly outside of the api client libraries.
 * Instead, you should use the methods provided by the api client libraries.
 */
const apiRequest = async (options) => {
    const { apiHostname, method = 'GET', raw, endpoint, token, body, headers = {} } = options;
    // Ensure the endpoint starts with `/`.
    // This allows us to easily join URLs when `$API_SERVER_URL` is set.
    /* istanbul ignore if */
    /* c8 ignore next 5 */
    if (process.env.NODE_ENV === 'development') {
        if (endpoint[0] !== '/') {
            throw new Error('Endpoint must begin with "/"');
        }
    }
    if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }
    if (!headers.Accept) {
        headers.Accept = 'application/json';
    }
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    const host = removeTrailingSlash__default.default(apiHostname);
    const res = await fetch(`${host}${endpoint}`, {
        method,
        headers,
        body: raw ? body : JSON.stringify(body),
        // We need to include cookies in all requests (for feature flags).
        credentials: 'include'
    });
    /* c8 ignore next 7 */
    if (raw) {
        if (res.ok) {
            return res;
        }
        else {
            throw new Error(`unexpected server error ${res.status}`);
        }
    }
    const type = res.headers.get('Content-Type');
    if (!type || !type.includes('json')) {
        // For whatever reason, the API didn't return JSON. We don't know what to do here, so log (and throw) an error.
        /* istanbul ignore next */
        /* c8 ignore next 4 */
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn('API (%s) returned non-JSON response:', endpoint, res);
        }
        throw new Error('Unexpected result from API');
    }
    const responseBody = await res.json();
    if (res.ok) {
        return responseBody;
    }
    let errorMessage;
    if (responseBody.validationErrors) {
        // The signup API (POST /api/users) returns `.validationErrors` in addition to `.error`.
        // Take the first validation error.
        // TODO: use something like npmjs.org/aggregate-error and display all validation errors.
        errorMessage = Object.values(responseBody.validationErrors)[0];
    }
    else if (responseBody.error) {
        // The API error handler returns the shape `{ error: string }`.
        errorMessage = responseBody.error;
    }
    else {
        // Throw a generic error if there was an issue with the error handler.
        errorMessage = 'Unexpected server error';
    }
    const error = new Error(errorMessage);
    error.status = res.status;
    error.error_code = responseBody.error_code;
    error.details = responseBody.details;
    throw error;
};

const getEnterpriseById = ({ apiHostname, token, enterpriseId }) => {
    return apiRequest({
        apiHostname,
        endpoint: `/api/billing/v2/enterprises/${enterpriseId}`,
        token
    });
};
const getEnterpriseMemberById = ({ apiHostname, token, enterpriseId }) => {
    return apiRequest({
        apiHostname,
        endpoint: `/api/billing/v2/enterprises/${enterpriseId}/members`,
        token
    });
};
const getIntegrationConnections = ({ apiHostname, token, enterpriseId }) => {
    return apiRequest({
        apiHostname,
        endpoint: `/api/integrations/enterprises/${enterpriseId}/connections`,
        token
    });
};
const getTemplates = ({ apiHostname, token, enterpriseId, connectionId }) => {
    const query = '?mapping_version=2&reload_schema=true';
    const endpoint = connectionId
        ? /* c8 ignore next */
            `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/templates${query}`
        : `/api/integrations/enterprises/${enterpriseId}/templates${query}`;
    return apiRequest({
        apiHostname,
        endpoint,
        token
    });
};
const getSentIssuesByIds = ({ apiHostname, token, enterpriseId, issueIds }) => {
    const issueIdsString = issueIds.join(',');
    const issueIdsAmount = issueIds.length;
    return apiRequest({
        apiHostname,
        endpoint: `/api/enterprises/${enterpriseId}/issue-metadata?issue_id=${issueIdsString}&limit=${issueIdsAmount}`,
        token
    });
};
const getProjectsAndIssueTypes = async ({ apiHostname, token, enterpriseId, connectionId }) => {
    const projectsAndIssueTypes = await apiRequest({
        apiHostname,
        endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/projects`,
        token
    });
    const projectsWithEmptyIssueTypes = projectsAndIssueTypes
        .filter(project => !project?.issueTypes?.length)
        .map(({ name }) => name);
    if (projectsWithEmptyIssueTypes.length) {
        // eslint-disable-next-line no-console
        console.warn('Can not get issue types for the following projects: ', projectsWithEmptyIssueTypes.join(', '));
    }
    const projectsWithoutCreateIssues = projectsAndIssueTypes
        .filter(project => !project.canCreateIssue)
        .map(({ name }) => name);
    if (projectsWithoutCreateIssues.length) {
        // eslint-disable-next-line no-console
        console.warn('User who installed the connection does not have permission to create issues for the following projects: ', projectsWithoutCreateIssues.join(', '));
    }
    return (projectsAndIssueTypes
        // Filter out issueTypes that have a property 'subtask' set to true
        .map(project => ({
        ...project,
        issueTypes: project.issueTypes?.filter(issueType => !issueType.subtask)
    }))
        // Filter out projects that don't have any issueTypes left after the previous filter or that don't have the canCreateIssue property set to true
        .filter(project => project.issueTypes &&
        project.issueTypes.length > 0 &&
        project.canCreateIssue));
};
const sendIssue = ({ apiHostname, token, enterpriseId, connectionId, body }) => {
    return apiRequest({
        apiHostname,
        endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/issues`,
        method: 'POST',
        token,
        body
    });
};
/** Get issues that can be used as parent issues for a project. */
const getParentIssues = async ({ apiHostname, token, enterpriseId, connectionId, projectId, search = '' }) => {
    return apiRequest({
        apiHostname,
        token,
        endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/issues?issue_types=epic&project_id=${projectId}&search_query=${encodeURIComponent(search)}`
    });
};
const getIssuesStatuses = async ({ apiHostname, token, enterpriseId, connectionId, issueIds, groupId }) => {
    const query = new URLSearchParams();
    if (issueIds) {
        query.set('issue_ids', issueIds.join(','));
    }
    if (groupId) {
        query.set('group_id', groupId);
    }
    return apiRequest({
        apiHostname,
        token,
        endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/issues/status?${query.toString()}`
    });
};

var AxeIssueTagFieldCategories;
(function (AxeIssueTagFieldCategories) {
    AxeIssueTagFieldCategories["AccessibilityStandards"] = "AccessibilityStandards";
    AxeIssueTagFieldCategories["Categories"] = "Categories";
    AxeIssueTagFieldCategories["IssueType"] = "IssueType";
    AxeIssueTagFieldCategories["WCAGSuccessCriteria"] = "WCAGSuccessCriteria";
})(AxeIssueTagFieldCategories || (AxeIssueTagFieldCategories = {}));
const JiraFieldMappersToExportKeys = {
    ShortText: 'jira-short-text-mapper',
    Labels: 'jira-labels-mapper',
    Number: 'jira-number-mapper',
    Url: 'jira-url-mapper',
    Date: 'jira-date-mapper',
    SingleOption: 'jira-single-option-mapper',
    MultiOptions: 'jira-multi-options-mapper',
    SecurityLevel: 'jira-security-level-mapper'
};
const JiraFieldMappersKeys = {
    ...JiraFieldMappersToExportKeys,
    Parent: 'jira-parent-mapper',
    EpicLink: 'jira-epic-link-mapper'
};

exports.IssueFieldSource = void 0;
(function (IssueFieldSource) {
    IssueFieldSource["Axe"] = "AxeIssue";
    IssueFieldSource["FixedValue"] = "fixed_value";
    IssueFieldSource["AxePartial"] = "AxePartial";
    IssueFieldSource["Combined"] = "Combined";
})(exports.IssueFieldSource || (exports.IssueFieldSource = {}));

const maxShortTextLength = 255;
const shortTextCustomType = 'com.atlassian.jira.plugin.system.customfieldtypes:textfield';
const toStringSafe = (value) => {
    if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
    }
    return value ? String(value) : '';
};
const generateAdfParagraph = (text) => {
    return {
        version: 1,
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                content: [
                    {
                        type: 'text',
                        text
                    }
                ]
            }
        ]
    };
};
class ShortTextMapper {
    id = JiraFieldMappersKeys.ShortText;
    isEnum = false;
    maxLength = maxShortTextLength;
    canMapThisField(jiraSchema) {
        const { type, custom } = jiraSchema.schema;
        const isCustomType = !!custom;
        const isString = type === 'string';
        return isCustomType ? isString && custom === shortTextCustomType : isString;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { hasDefaultValue, defaultValue } = fieldSchema;
        const hasAllowedMapping = !!fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.Axe;
        let value;
        // If we have a mapping, use it and ignore all else
        if (hasAllowedMapping) {
            value = toStringSafe(axeIssue[fieldMapping.source]);
        }
        else if (hasDefaultValue) {
            value = toStringSafe(defaultValue);
        }
        return {
            value,
            uiFieldType: 'text'
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema, integrationSlug }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        // If value is provided, check that it's valid
        if (value.length > maxShortTextLength) {
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: false,
                    isInvalidValue: false,
                    isLengthTooLong: true
                }
            };
        }
        const isEnvironmentField = rawSchema.key === 'environment';
        const isAdfFormatRequired = integrationSlug === billingUtils.ProductSlugs.jiraDirectCloudIntegration;
        if (isEnvironmentField && isAdfFormatRequired) {
            return {
                integrationValue: generateAdfParagraph(value)
            };
        }
        return {
            integrationValue: value.replace(/\n/g, ' ')
        };
    }
}

const JiraTypeDate = 'date';
const JiraTypeDateTime = 'datetime';
const dateCustomTypes = [
    'com.atlassian.jira.plugin.system.customfieldtypes:datepicker',
    'com.atlassian.jira.plugin.system.customfieldtypes:datetime'
];
class DateMapper {
    id = JiraFieldMappersKeys.Date;
    validTypes = [JiraTypeDate, JiraTypeDateTime];
    isEnum = false;
    canMapThisField(jiraSchema) {
        const { type, custom } = jiraSchema.schema;
        const isDate = this.validTypes.includes(type);
        const isCustom = !!custom;
        return isCustom ? isDate && dateCustomTypes.includes(custom) : isDate;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { schema: { type }, defaultValue, hasDefaultValue } = fieldSchema;
        const uiFieldType = type === JiraTypeDate ? 'date' : 'datetime-local';
        const setAllowedMapping = fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.Axe;
        const hasIntegrationDefault = hasDefaultValue && defaultValue;
        let value;
        // If we have a mapping, use it and ignore all else
        if (setAllowedMapping) {
            value = toStringSafe(axeIssue[fieldMapping.source]);
        }
        else if (hasIntegrationDefault) {
            value = defaultValue;
        }
        return {
            uiFieldType,
            value: value && this.formatDateStringToHTMLRequirements(value, uiFieldType)
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema, uiFieldType }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        if (uiFieldType === JiraTypeDate) {
            // we always get this value from datepicker here it should be in YYYY-MM-DD format
            const isDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
            if (!isDate) {
                return {
                    integrationValue: undefined,
                    error: {
                        isMissingRequired: false,
                        isInvalidValue: true,
                        isLengthTooLong: false
                    }
                };
            }
            return {
                integrationValue: value
            };
        }
        const normalizedValue = this.ensureUtcSuffix(value);
        const date = new Date(normalizedValue);
        if (isNaN(date.valueOf())) {
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: false,
                    isInvalidValue: true,
                    isLengthTooLong: false
                }
            };
        }
        return {
            integrationValue: this.formatDateTimeWithOffset(normalizedValue)
        };
    }
    formatDateStringToHTMLRequirements(dateAsString, uiFieldType) {
        if (!dateAsString || dateAsString.length === 0) {
            return '';
        }
        // Normalize timezone-less inputs to UTC to avoid environment-dependent shifts.
        const normalizedDateStr = this.ensureUtcSuffix(dateAsString);
        const date = new Date(normalizedDateStr);
        if (isNaN(date.valueOf())) {
            return '';
        }
        return uiFieldType === JiraTypeDate
            ? date.toISOString().slice(0, 10) // Returns 'YYYY-MM-DD'
            : date.toISOString().slice(0, 19); // Returns 'YYYY-MM-DDTHH:MM'
    }
    ensureUtcSuffix(dateStr) {
        const hasTimezone = /(Z|z|[+|-]\d{2}:\d{2}|[+|-]\d{4})$/.test(dateStr);
        if (hasTimezone) {
            return dateStr;
        }
        return `${dateStr}Z`;
    }
    // Jira need a specific formatting for datetime with +0000 at the end
    formatDateTimeWithOffset(dateStr) {
        const date = new Date(dateStr);
        // Format UTC part
        const yyyy = date.getUTCFullYear();
        const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(date.getUTCDate()).padStart(2, '0');
        const hh = String(date.getUTCHours()).padStart(2, '0');
        const mi = String(date.getUTCMinutes()).padStart(2, '0');
        const ss = String(date.getUTCSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}.000+0000`;
    }
}

const epicLinkCustomType = 'com.pyxis.greenhopper.jira:gh-epic-link';
/** Epic Link field used in Jira On-Prem to set relation to an epic */
class EpicLinkMapper {
    id = JiraFieldMappersKeys.EpicLink;
    isEnum = false;
    canMapThisField(jiraSchema) {
        const { custom } = jiraSchema.schema;
        return custom === epicLinkCustomType;
    }
    getDisplayValue() {
        return {
            uiFieldType: 'async-select'
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        if (value) {
            return {
                // value should be the `key` of the ticket, e.g. TJP-1
                integrationValue: value
            };
        }
        if (!rawSchema.required) {
            return {
                integrationValue: undefined
            };
        }
        return {
            integrationValue: undefined,
            error: {
                isMissingRequired: true,
                isInvalidValue: false,
                isLengthTooLong: false
            }
        };
    }
}

const maxLabelLength = 255;
const labelsCustomType = 'com.atlassian.jira.plugin.system.customfieldtypes:labels';
const TAGS_CATEGORIES = Object.values(AxeIssueTagFieldCategories);
const TagCategories = {
    [AxeIssueTagFieldCategories.AccessibilityStandards]: [
        'wcag2a',
        'wcag2aa',
        'wcag2aaa',
        'wcag21a',
        'wcag21aa',
        'wcag22aa',
        'wcag2a-obsolete',
        'ACT',
        'section508',
        /^section508\..*\..*/i,
        'TTv5',
        /^TT.*\..*$/i,
        'EN-301-549',
        /^EN-9\..*/i,
        'RGAAv4',
        /^RGAA-.*\..*\..*/i
    ],
    [AxeIssueTagFieldCategories.Categories]: [
        'cat.aria',
        'cat.color',
        'cat.forms',
        'cat.keyboard',
        'cat.language',
        'cat.name-role-value',
        'cat.parsing',
        'cat.semantics',
        'cat.sensory-and-visual-cues',
        'cat.structure',
        'cat.tables',
        'cat.text-alternatives',
        'cat.time-and-media'
    ],
    [AxeIssueTagFieldCategories.IssueType]: [
        'best-practice',
        'experimental',
        'needs review'
    ],
    [AxeIssueTagFieldCategories.WCAGSuccessCriteria]: [/^wcag\d{3}$/i]
};
class LabelsMapper {
    id = JiraFieldMappersKeys.Labels;
    maxLength = maxLabelLength;
    isEnum = false;
    labelDelimiter = ',';
    canMapThisField(jiraSchema) {
        const { type, custom, items, system } = jiraSchema.schema;
        const isArrayOfString = type === 'array' && items === 'string';
        return (isArrayOfString && (system === 'labels' || custom === labelsCustomType));
    }
    formatLabelValueForUI(value) {
        if (typeof value === 'string') {
            const formattedValue = value
                .split(this.labelDelimiter)
                .map(label => label.trim())
                .join(this.labelDelimiter);
            return formattedValue;
        }
        else if (Array.isArray(value)) {
            return value.flat().join(this.labelDelimiter);
        }
        else if (value instanceof Object) {
            return JSON.stringify(value);
        }
        return '' + value;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { defaultValue, hasDefaultValue } = fieldSchema;
        let value;
        // If we have a mapping, use it and ignore all else
        if (fieldMapping) {
            value = this.getDisplayValueBySourceType(fieldMapping, axeIssue);
        }
        else if (hasDefaultValue && defaultValue) {
            value = defaultValue;
        }
        return {
            value: value && this.formatLabelValueForUI(value),
            uiFieldType: 'text'
        };
    }
    getDisplayValueBySourceType(fieldMapping, axeIssue) {
        return fieldMapping.sourceType === exports.IssueFieldSource.Combined
            ? this.getCombinedFieldValue(fieldMapping, axeIssue)
            : this.getMappedFieldValueBySourceType(fieldMapping, axeIssue);
    }
    getMappedFieldValueBySourceType(fieldMapping, axeIssue) {
        switch (fieldMapping.sourceType) {
            case exports.IssueFieldSource.Axe:
                return axeIssue[fieldMapping.source];
            case exports.IssueFieldSource.FixedValue:
                return fieldMapping.source;
            case exports.IssueFieldSource.AxePartial:
                return this.getAxePartialFieldValue(fieldMapping, axeIssue);
            default:
                return '';
        }
    }
    getCombinedFieldValue(fieldMapping, axeIssue) {
        const contentParts = fieldMapping.source.split(',');
        const labelsSet = new Set();
        contentParts.forEach(part => {
            const splittedParts = part.split(':');
            const sourceType = splittedParts[0];
            const source = sourceType === exports.IssueFieldSource.AxePartial
                ? splittedParts.slice(1).join(':')
                : splittedParts[1];
            const mappedValue = this.getMappedFieldValueBySourceType({ sourceType, source }, axeIssue);
            if (mappedValue) {
                if (Array.isArray(mappedValue)) {
                    mappedValue.forEach(value => {
                        labelsSet.add(value);
                    });
                }
                else {
                    labelsSet.add(mappedValue);
                }
            }
        });
        return Array.from(labelsSet);
    }
    getAxePartialFieldValue(fieldMapping, axeIssue) {
        const [field, category] = fieldMapping.source.split(':');
        if (field.toLowerCase() !== 'tags' ||
            !TAGS_CATEGORIES.includes(category) ||
            !axeIssue?.tags) {
            return [];
        }
        const allowedTags = TagCategories[category];
        const filteredTags = axeIssue.tags.filter((tag) => allowedTags.some(allowedTag => typeof allowedTag === 'string'
            ? tag.toLowerCase() === allowedTag.toLowerCase()
            : allowedTag.test(tag)));
        // JIRA does not allow spaces in labels, but `needs review` include a space.
        const isSomeTagsContainsSpace = filteredTags.some((tag) => tag.includes(' '));
        return !isSomeTagsContainsSpace
            ? filteredTags
            : filteredTags.map((tag) => tag.replace(/ /g, '-'));
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return { integrationValue: undefined };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        // If value is provided, check that it's valid
        if (value.length > maxLabelLength) {
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: false,
                    isInvalidValue: false,
                    isLengthTooLong: true
                }
            };
        }
        // If value is provided, check that it's valid
        if (value.includes(' ')) {
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: false,
                    isInvalidValue: true,
                    isLengthTooLong: false
                }
            };
        }
        return {
            integrationValue: value.split(',')
        };
    }
}

const multiOptionsCustomTypes = [
    'com.atlassian.jira.plugin.system.customfieldtypes:multiselect',
    'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes'
];
var SupportedItems;
(function (SupportedItems) {
    SupportedItems["Version"] = "version";
    SupportedItems["Option"] = "option";
})(SupportedItems || (SupportedItems = {}));
class MultiOptionsMapper {
    id = JiraFieldMappersKeys.MultiOptions;
    isEnum = true;
    canMapThisField(jiraSchema) {
        const isArrayOfOptions = jiraSchema.schema.type === 'array';
        return isArrayOfOptions
            ? this.canMapThisArrayItems(jiraSchema.schema)
            : false;
    }
    canMapThisArrayItems(schema) {
        const { items, custom } = schema;
        switch (items) {
            case SupportedItems.Version:
                return true;
            case SupportedItems.Option:
                return !!custom && multiOptionsCustomTypes.includes(custom);
            default:
                return false;
        }
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { hasDefaultValue, defaultValue, allowedValues: fieldAllowedValues } = fieldSchema;
        const uiFieldType = 'multiselect';
        const allowedValues = fieldAllowedValues?.map(({ id, value, name }) => ({
            id,
            name: value || name
        }));
        const hasAllowedMapping = !!fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.FixedValue;
        const hasValidDefaultValue = hasDefaultValue &&
            defaultValue &&
            Array.isArray(defaultValue) &&
            defaultValue.length > 0;
        let value;
        // If we have a mapping, use it and ignore all else
        if (hasAllowedMapping &&
            fieldMapping.sourceType === exports.IssueFieldSource.FixedValue) {
            value = allowedValues?.find(({ id }) => id === fieldMapping.source)?.id;
        }
        else if (hasValidDefaultValue) {
            value = defaultValue[0].id;
        }
        return {
            uiFieldType,
            allowedValues,
            value
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        // Find definition for selected value
        const selectedAllowedValue = rawSchema.allowedValues?.find(allowedValue => allowedValue.id === value);
        if (!selectedAllowedValue) {
            // If we could not find we will just tread as if no value was selected.
            // The other option is to treat as invalid value, but user will not be able to fix. So we do not want to do this.
            return {
                integrationValue: undefined
            };
        }
        return {
            integrationValue: [selectedAllowedValue]
        };
    }
}

const numberCustomType = 'com.atlassian.jira.plugin.system.customfieldtypes:float';
class NumberMapper {
    id = JiraFieldMappersKeys.Number;
    isEnum = false;
    canMapThisField(jiraSchema) {
        const { type, custom } = jiraSchema.schema;
        const isCustomType = !!custom;
        const isNumber = type === 'number';
        return isCustomType ? isNumber && custom === numberCustomType : isNumber;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { hasDefaultValue, defaultValue } = fieldSchema;
        const hasAllowedMapping = !!fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.Axe;
        let value;
        // If we have a mapping, use it and ignore all else
        if (hasAllowedMapping) {
            const mappedValue = toStringSafe(axeIssue[fieldMapping.source]);
            value = Number.isNaN(Number(mappedValue)) ? undefined : mappedValue;
        }
        else if (hasDefaultValue && defaultValue) {
            value = String(defaultValue);
        }
        return {
            value,
            uiFieldType: 'number'
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        const numberValue = Number(value);
        if (!isNaN(numberValue)) {
            return {
                integrationValue: numberValue
            };
        }
        return {
            integrationValue: undefined,
            error: {
                isMissingRequired: false,
                isInvalidValue: true,
                isLengthTooLong: false
            }
        };
    }
}

const parentSystemType = 'parent';
const parentType = 'issuelink';
/**
 * Parent field used in Jira Cloud to set relation to a parent issue
 */
class ParentMapper {
    id = JiraFieldMappersKeys.Parent;
    isEnum = false;
    canMapThisField(jiraSchema) {
        const { type, system } = jiraSchema.schema;
        return type === parentType && system === parentSystemType;
    }
    getDisplayValue() {
        return { uiFieldType: 'async-select' };
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        if (value) {
            return {
                integrationValue: { key: value }
            };
        }
        if (!rawSchema.required) {
            return {
                integrationValue: undefined
            };
        }
        return {
            integrationValue: undefined,
            error: {
                isMissingRequired: true,
                isInvalidValue: false,
                isLengthTooLong: false
            }
        };
    }
}

class SecurityLevelMapper {
    id = JiraFieldMappersKeys.SecurityLevel;
    isEnum = true;
    canMapThisField(jiraSchema) {
        const { type, system } = jiraSchema.schema;
        const isSecurityLevelType = type === 'securitylevel';
        const isSystem = system === 'security';
        return isSecurityLevelType && isSystem;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { hasDefaultValue, defaultValue, allowedValues: integrationAllowedValues } = fieldSchema;
        const hasAllowedMapping = !!fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.FixedValue;
        const allowedValues = integrationAllowedValues?.map(({ id, name }) => ({
            id,
            name
        }));
        let value;
        // If we have a mapping, use it and ignore all else
        if (hasAllowedMapping) {
            value = allowedValues?.find(({ id }) => id === fieldMapping.source)?.id;
        }
        else if (hasDefaultValue && defaultValue) {
            value = defaultValue.id;
        }
        return {
            value,
            uiFieldType: 'select',
            allowedValues
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        // Find definition for selected value
        const selectedAllowedValue = rawSchema.allowedValues?.find(({ id }) => id === value);
        if (!selectedAllowedValue) {
            // If we could not find we will just tread as if no value was selected.
            // The other option is to treat as invalid value, but user will not be able to fix. So we do not want to do this.
            return {
                integrationValue: undefined
            };
        }
        return {
            integrationValue: selectedAllowedValue
        };
    }
}

const singleOptionCustomTypes = [
    'com.atlassian.jira.plugin.system.customfieldtypes:radiobuttons',
    'com.atlassian.jira.plugin.system.customfieldtypes:select'
];
class SingleOptionMapper {
    id = JiraFieldMappersKeys.SingleOption;
    isEnum = true;
    canMapThisField(jiraSchema) {
        const { type, custom } = jiraSchema.schema;
        const isOption = type === 'option';
        const isValidCustomType = !!custom && singleOptionCustomTypes.includes(custom);
        return isOption && isValidCustomType;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { hasDefaultValue, defaultValue, allowedValues } = fieldSchema;
        const hasAllowedMapping = !!fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.FixedValue;
        const uiAllowedValues = allowedValues?.map(({ id, value }) => ({
            id,
            name: value
        }));
        let value;
        // If we have a mapping, use it and ignore all else
        if (hasAllowedMapping) {
            value = uiAllowedValues?.find(({ id }) => id === fieldMapping.source)?.id;
        }
        else if (hasDefaultValue && defaultValue) {
            value = defaultValue.id;
        }
        return {
            value,
            uiFieldType: 'select',
            allowedValues: uiAllowedValues
        };
    }
    convertDisplayToIntegrationValue({ value, rawSchema }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        // Find definition for selected value
        const selectedAllowedValue = rawSchema.allowedValues?.find(({ id }) => id === value);
        if (!selectedAllowedValue) {
            // If we could not find we will just tread as if no value was selected.
            // The other option is to treat as invalid value, but user will not be able to fix. So we do not want to do this.
            return {
                integrationValue: undefined
            };
        }
        return {
            integrationValue: selectedAllowedValue
        };
    }
}

const maxUrlLength = 255;
const urlRegExp = /^http(s)*:\/{2}/;
const urlCustomType = 'com.atlassian.jira.plugin.system.customfieldtypes:url';
class UrlMapper {
    id = JiraFieldMappersKeys.Url;
    maxLength = maxUrlLength;
    isEnum = false;
    canMapThisField(jiraSchema) {
        const { type, custom } = jiraSchema.schema;
        const isCustomType = !!custom;
        const isString = type === 'string';
        return isString && isCustomType && custom === urlCustomType;
    }
    getDisplayValue(fieldSchema, axeIssue, fieldMapping) {
        const { hasDefaultValue, defaultValue } = fieldSchema;
        const hasAllowedMapping = !!fieldMapping && fieldMapping.sourceType === exports.IssueFieldSource.Axe;
        let value;
        // If we have a mapping, use it and ignore all else
        if (hasAllowedMapping) {
            value = toStringSafe(axeIssue[fieldMapping.source]);
        }
        else if (hasDefaultValue && defaultValue) {
            value = defaultValue;
        }
        return {
            value,
            uiFieldType: 'text'
        };
    }
    convertDisplayToIntegrationValue({ rawSchema, value }) {
        // Check for a case when there is no value provided
        if (!value) {
            if (!rawSchema.required) {
                return {
                    integrationValue: undefined
                };
            }
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: true,
                    isInvalidValue: false,
                    isLengthTooLong: false
                }
            };
        }
        // If value is provided, check that it's valid
        if (value.length > maxUrlLength) {
            return {
                integrationValue: undefined,
                error: {
                    isMissingRequired: false,
                    isInvalidValue: false,
                    isLengthTooLong: true
                }
            };
        }
        const isValidValue = urlRegExp.test(value);
        if (isValidValue) {
            return {
                integrationValue: value
            };
        }
        return {
            integrationValue: undefined,
            error: {
                isMissingRequired: false,
                isInvalidValue: true,
                isLengthTooLong: false
            }
        };
    }
}

const JiraShortTextMapper = new ShortTextMapper();
const JiraNumberMapper = new NumberMapper();
const JiraLabelsMapper = new LabelsMapper();
const JiraUrlMapper = new UrlMapper();
const JiraDateMapper = new DateMapper();
const JiraSingleOptionMapper = new SingleOptionMapper();
const JiraMultiOptionsMapper = new MultiOptionsMapper();
const JiraParentMapper = new ParentMapper();
const JiraEpicLinkMapper = new EpicLinkMapper();
const JiraSecurityLevelMapper = new SecurityLevelMapper();
const exportedJiraFieldMappers = {
    [JiraFieldMappersKeys.ShortText]: JiraShortTextMapper,
    [JiraFieldMappersKeys.Number]: JiraNumberMapper,
    [JiraFieldMappersKeys.Labels]: JiraLabelsMapper,
    [JiraFieldMappersKeys.Url]: JiraUrlMapper,
    [JiraFieldMappersKeys.Date]: JiraDateMapper,
    [JiraFieldMappersKeys.SingleOption]: JiraSingleOptionMapper,
    [JiraFieldMappersKeys.MultiOptions]: JiraMultiOptionsMapper,
    [JiraFieldMappersKeys.SecurityLevel]: JiraSecurityLevelMapper
};
const JiraFieldMappers = {
    ...exportedJiraFieldMappers,
    [JiraFieldMappersKeys.Parent]: JiraParentMapper,
    [JiraFieldMappersKeys.EpicLink]: JiraEpicLinkMapper
};

// these fields are processed on server side, as required special handling
const excludedFromDisplay = ['description'];
const prepareUIFields = (template, axeIssue) => {
    const mappers = Object.values(JiraFieldMappers);
    const v2Mapping = template.fieldMappings;
    const uiFields = {
        requiredFields: [],
        nonRequiredFields: [],
        hasNotEditableRequiredFields: false
    };
    for (const integrationField of v2Mapping.fields) {
        // Jira Data Center field does not have a key, so we use the fieldId
        const fieldKey = integrationField.key || integrationField.fieldId;
        if (excludedFromDisplay.includes(fieldKey)) {
            continue;
        }
        // Look for mapping for this field
        const fieldMapping = v2Mapping.mapping[fieldKey];
        let mapper = undefined;
        // First we will try to get mapper from the mapping definition
        if (fieldMapping) {
            mapper = JiraFieldMappers[fieldMapping.mapperId];
        }
        // If we still do not have a mapper, try to determine which one we can use
        if (!mapper) {
            mapper = mappers.find(mapperToCheck => mapperToCheck.canMapThisField(integrationField));
        }
        // At this point we must have a mapper in order to be able to show this field
        if (mapper) {
            const { value, uiFieldType, allowedValues } = mapper.getDisplayValue(integrationField, axeIssue, fieldMapping);
            const isSummaryField = fieldKey === 'summary';
            const fieldValue = isSummaryField && template?.summary_prefix
                ? `${template.summary_prefix} ${value || ''}`
                : value;
            const uiFieldDefinition = {
                key: fieldKey,
                name: integrationField.name,
                uiFieldType,
                value: fieldValue,
                mapper,
                rawSchema: integrationField,
                allowedValues,
                integrationSlug: template.connection.productSlug
            };
            if (integrationField.required) {
                uiFields.requiredFields.push(uiFieldDefinition);
            }
            else {
                uiFields.nonRequiredFields.push(uiFieldDefinition);
            }
        }
        else if (integrationField.required) {
            uiFields.hasNotEditableRequiredFields = true;
        }
    }
    return uiFields;
};
const prepareUIFieldsForSubmission = (uiFields) => {
    const fieldData = {};
    for (const requiredField of uiFields.requiredFields) {
        if (requiredField.integrationValue) {
            fieldData[requiredField.key] = requiredField.integrationValue;
        }
    }
    for (const nonRequiredField of uiFields.nonRequiredFields) {
        if (nonRequiredField.integrationValue) {
            fieldData[nonRequiredField.key] = nonRequiredField.integrationValue;
        }
    }
    return fieldData;
};
const requiredFields = ['description'];
const getMissedRequiredFields = (template) => {
    const v2Mapping = template.fieldMappings;
    const missedFields = requiredFields.filter(field => !v2Mapping.fields.find(f => f.key === field || f.fieldId === field));
    return missedFields;
};

function useCachedSearch({ cacheKey, query, fetch }) {
    const [data, setData] = React.useState(null);
    const [cache, setCache] = React.useState({});
    const fetchRef = React.useRef(fetch);
    const [fetchVersion, setFetchVersion] = React.useState(0);
    React.useEffect(() => {
        if (fetchRef.current === fetch) {
            return;
        }
        // Clear cache if fetch function changed
        setCache({});
        setData(null);
        fetchRef.current = fetch;
        setFetchVersion(prev => prev + 1);
    }, [fetch]);
    React.useEffect(() => {
        if (!cacheKey) {
            return;
        }
        const cachedData = cache[cacheKey];
        if (cachedData) {
            setData(cachedData);
            return;
        }
        const getData = async () => {
            const result = await fetch(query);
            setCache(prevCache => ({
                ...prevCache,
                [cacheKey]: result
            }));
            setData(result);
        };
        getData();
    }, [query, fetchVersion, cacheKey]);
    const updateCache = React.useCallback((cacheKeyToUpdate, result) => {
        setCache(prevCache => ({
            ...prevCache,
            [cacheKeyToUpdate]: result
        }));
    }, []);
    return { data, updateCache, cache };
}

const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debouncedValue;
};

var modules_51047e93 = {"comboboxContainer":"_comboboxContainer_90164dc3"};

var modules_6908cf4a = {"chooseTemplateStep":"_chooseTemplateStep_2feda507","descriptionMatch":"_descriptionMatch_2feda507","disabledOption":"_disabledOption_2feda507"};

const TextWithMatches = ({ text, inputValue }) => {
    const matchStart = text.toLowerCase().indexOf(inputValue.toLowerCase());
    if (matchStart === -1) {
        return jsxRuntime.jsx("span", { children: text });
    }
    const matchLength = inputValue.length;
    const matchBefore = text.substring(0, matchStart);
    const match = text.substring(matchStart, matchLength + matchStart);
    const matchAfter = text.substring(matchStart + matchLength);
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("span", { children: matchBefore }), jsxRuntime.jsx("em", { className: modules_6908cf4a.descriptionMatch, "data-testid": "match", children: match }), jsxRuntime.jsx("span", { children: matchAfter })] }));
};
const hasMatch = (string, subString) => string.toLowerCase().includes(subString.toLowerCase());
const getTemplateDescription = (template, projectsByConnections, t) => {
    const selectedConnection = template.connection.id || '';
    const baseUrl = template.connection.baseUrl;
    const project = projectsByConnections[selectedConnection]?.find(({ id }) => template.project_id === id);
    const issueType = project?.issueTypes?.find(({ id }) => template.issue_type_id === id);
    const projectName = project?.name || t('Not found');
    const issueTypeName = issueType?.name || t('Not found');
    return `${baseUrl} > ${projectName} > ${issueTypeName}`;
};
const SelectTemplate = ({ integrationData, hostProductName, multiple = false, loading, loadingError, allConnectionsTemplates, projectsByConnections, selectedTemplate, setSelectedTemplate, templateError, connectionIdsWithLoadingErrors }) => {
    const { t } = useTranslation();
    const selectedOptionRef = React.useRef(null);
    const inputRef = React.useCallback((element) => {
        if (templateError && element) {
            element.focus();
        }
    }, [templateError]);
    const [inputValue, setInputValue] = React.useState(allConnectionsTemplates?.find(({ templateId }) => selectedTemplate?.templateId === templateId)?.name);
    const [matchedTemplates, setMatchedTemplates] = React.useState([]);
    const [autoSelectedOption, setAutoSelectedOption] = React.useState();
    const templateOptions = React.useMemo(() => {
        if (!allConnectionsTemplates ||
            !Object.keys(projectsByConnections).length) {
            return null;
        }
        setMatchedTemplates([]);
        const templatesToDisplay = allConnectionsTemplates.filter(template => !connectionIdsWithLoadingErrors.includes(template.connection.id));
        //array of ComboboxOption filtered by existence matches input value with option's title and/or description
        const comboboxFilteredOptions = templatesToDisplay.reduce((options, template) => {
            const onClick = e => {
                // stop the event from bubbling up to the prevent opening options list when autocompleting input value
                // remove after searching by description and titles will be added to the Cauldron combobox
                e.stopPropagation();
                setSelectedTemplate(template);
                setInputValue(template.name);
                setAutoSelectedOption(undefined);
            };
            const descriptionText = getTemplateDescription(template, projectsByConnections, t);
            const isProjectOrIssueTypeNotFound = descriptionText.includes('Not found');
            const baseComboboxOptionProps = {
                value: template.name,
                formValue: template.templateId,
                className: isProjectOrIssueTypeNotFound
                    ? modules_6908cf4a.disabledOption
                    : undefined,
                disabled: isProjectOrIssueTypeNotFound,
                onClick: isProjectOrIssueTypeNotFound ? undefined : onClick,
                ref: autoSelectedOption === template.templateId
                    ? selectedOptionRef
                    : undefined
            };
            // add all possible options to display in the dropdown
            if (!inputValue || inputValue === selectedTemplate?.name) {
                setMatchedTemplates(prev => [...prev, template]);
                options.push(jsxRuntime.jsx(cauldronReact.ComboboxOption, { ...baseComboboxOptionProps, description: descriptionText, children: template.name }, template.templateId));
                return options;
            }
            const hasDescriptionMatch = hasMatch(descriptionText, inputValue);
            const hasOptionTitleMatch = hasMatch(template.name, inputValue);
            if (!hasOptionTitleMatch && !hasDescriptionMatch) {
                return options;
            }
            setMatchedTemplates(prev => [...prev, template]);
            const description = hasDescriptionMatch ? (jsxRuntime.jsx(TextWithMatches, { text: descriptionText, inputValue: inputValue })) : (descriptionText);
            options.push(jsxRuntime.jsx(cauldronReact.ComboboxOption, { ...baseComboboxOptionProps, description: description, children: template.name }, template.templateId));
            return options;
        }, []);
        return comboboxFilteredOptions;
    }, [allConnectionsTemplates, inputValue, projectsByConnections]);
    React.useEffect(() => {
        if (autoSelectedOption && selectedOptionRef.current) {
            selectedOptionRef.current?.click();
        }
    }, [autoSelectedOption, selectedOptionRef.current]);
    if (loading) {
        const loadingLabel = multiple
            ? t('Share all to {{ capitalizedIntegrationName }} modal loading', {
                capitalizedIntegrationName: integrationData.productName
            })
            : t('Share to {{ capitalizedIntegrationName }} modal loading', {
                capitalizedIntegrationName: integrationData.productName
            });
        return (jsxRuntime.jsx("div", { className: modules_6908cf4a.chooseTemplateStep, children: jsxRuntime.jsx(cauldronReact.Loader, { label: loadingLabel }) }));
    }
    if (loadingError && !Object.keys(projectsByConnections).length) {
        const errorMessage = t('Error loading project, issue type, and/or template data');
        return (jsxRuntime.jsx("div", { className: modules_6908cf4a.chooseTemplateStep, children: jsxRuntime.jsx("p", { children: errorMessage }) }));
    }
    const onInput = (e) => {
        setInputValue(e.currentTarget.value);
    };
    const onBlur = () => {
        // if user has selected any template before, use this template
        if (selectedTemplate) {
            const selectedTemplateName = allConnectionsTemplates?.find(({ templateId }) => templateId === selectedTemplate?.templateId)?.name || '';
            setInputValue(selectedTemplateName);
        }
        //if user input something and has not selected any template before, set first matched template as selected
        if (inputValue && !selectedTemplate && matchedTemplates.length) {
            setInputValue(matchedTemplates[0].name || '');
            setAutoSelectedOption(matchedTemplates[0].templateId);
        }
    };
    return (jsxRuntime.jsxs("div", { className: modules_6908cf4a.chooseTemplateStep, children: [jsxRuntime.jsx("p", { children: multiple
                    ? t(`Select the template to send the {{ axe }} issues to. This will create one {{ capitalizedIntegrationName }} ticket with all {{ axe }} filtered issues.`, {
                        axe: hostProductName,
                        capitalizedIntegrationName: integrationData.productName
                    })
                    : t(`Select the template to send the {{ axe }} issue to. This will create one {{ capitalizedIntegrationName }} ticket for the selected {{ axe }} issue.`, {
                        axe: hostProductName,
                        capitalizedIntegrationName: integrationData.productName
                    }) }), jsxRuntime.jsx(cauldronReact.Combobox, { label: t('{{capitalizedIntegrationName}} Template', {
                    capitalizedIntegrationName: integrationData.productName
                }), value: inputValue, disabled: !allConnectionsTemplates?.length, error: templateError, inputRef: inputRef, onInput: onInput, placeholder: t('Choose a template'), required: true, autocomplete: "none", "aria-autocomplete": "list", onBlur: onBlur, onClick: () => setInputValue(''), renderNoResults: () => t('No results found'), children: templateOptions })] }));
};

const getCacheKey = (projectId, connectionId, search) => {
    return projectId && connectionId
        ? `${projectId}-${connectionId}-${search}`
        : '';
};
const isParentIssueMatch = (item, inputValue) => {
    if (!item) {
        return false;
    }
    const { key, fields: { summary } } = item;
    return (key.toLowerCase().includes(inputValue.toLowerCase()) ||
        summary.toLowerCase().includes(inputValue.toLowerCase()));
};
const getUniqueCachedItems = (cache) => {
    const issueKeyValueMap = new Map();
    const allCachedItems = Object.values(cache).flat();
    allCachedItems.forEach(item => {
        if (item) {
            issueKeyValueMap.set(item.key, item);
        }
    });
    return Array.from(issueKeyValueMap.values());
};
const SelectParentIssueComponent = ({ selectedTemplate, uiField, setUIField }) => {
    const { t } = useTranslation();
    const { userEnterpriseId, apiHostname, authFn } = useApiConfigContext();
    const [foundResults, setFoundResults] = React.useState([]);
    const [inputValue, setInputValue] = React.useState(() => uiField.value ?? '');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const debouncedInputValue = useDebounce(inputValue);
    const projectId = selectedTemplate?.project_id || '';
    const connectionId = selectedTemplate?.connection.id || '';
    const fetchParentIssues = React.useCallback(async (value) => {
        if (!userEnterpriseId || !projectId || !connectionId || loading) {
            return;
        }
        setLoading(true);
        setError(null);
        let result;
        try {
            const token = await authFn();
            result = await getParentIssues({
                enterpriseId: userEnterpriseId,
                connectionId,
                projectId,
                apiHostname,
                token: token || '',
                search: value
            });
        }
        catch (err) {
            setError(err);
        }
        finally {
            setLoading(false);
        }
        return result;
    }, [userEnterpriseId, projectId, connectionId, apiHostname, authFn]);
    const { data, updateCache, cache } = useCachedSearch({
        query: debouncedInputValue,
        fetch: fetchParentIssues,
        cacheKey: getCacheKey(projectId, connectionId, debouncedInputValue)
    });
    React.useEffect(() => {
        if (data?.length) {
            setFoundResults(data ?? []);
            return;
        }
        const allCachedResults = getUniqueCachedItems(cache);
        setFoundResults(allCachedResults);
    }, [data, cache, inputValue]);
    const options = React.useMemo(() => foundResults.filter(item => isParentIssueMatch(item, inputValue)), [foundResults, inputValue]);
    const onChange = (e) => {
        setInputValue(e.target.value);
    };
    const onSelectionChange = React.useCallback(({ value }) => {
        uiField.value = value;
        setUIField({ ...uiField });
        setInputValue(value ?? '');
        const cacheKey = getCacheKey(projectId, connectionId, value ?? '');
        const selectedIssue = foundResults?.find(({ key }) => key === value);
        updateCache(cacheKey, selectedIssue ? [selectedIssue] : []);
    }, [uiField, projectId, connectionId, foundResults]);
    const noResults = React.useCallback(() => {
        if (loading) {
            return jsxRuntime.jsx(jsxRuntime.Fragment, {});
        }
        return (jsxRuntime.jsx("div", { children: options?.length === 0
                ? t('No results found. Try using the full word or issue key.')
                : t('No results found') }));
    }, [loading, options.length]);
    const onBlur = React.useCallback(() => {
        if (!uiField.value) {
            return;
        }
        setInputValue(uiField.value);
    }, [uiField.value]);
    const clearInput = React.useCallback(() => setInputValue(''), []);
    return (jsxRuntime.jsx("div", { className: modules_51047e93.comboboxContainer, children: jsxRuntime.jsxs(cauldronReact.Combobox, { autocomplete: "none", "aria-autocomplete": "list", label: t('Parent Issue'), "data-testid": uiField.key, value: inputValue, onBlur: onBlur, required: uiField.rawSchema.required, requiredText: uiField.rawSchema.required ? t('Required') : '', onChange: onChange, onFocus: clearInput, onClick: clearInput, onSelectionChange: onSelectionChange, placeholder: t('Search by issue key or summary'), renderNoResults: noResults, error: error ? t('Something went wrong while searching') : undefined, children: [loading && !options?.length ? (jsxRuntime.jsx(cauldronReact.Loader, { label: t('Searching...') })) : null, options.map(({ key, fields }) => (jsxRuntime.jsx(cauldronReact.ComboboxOption, { value: key, description: jsxRuntime.jsx(TextWithMatches, { text: fields.summary, inputValue: inputValue }), children: key }, key)))] }) }));
};

const ProvideValuesComponent = ({ uiField, isRequired, setUIField, selectedTemplate }) => {
    const { t } = useTranslation();
    const selectRef = React.useRef(null);
    const inputRef = React.useRef(null);
    let error = undefined;
    if (uiField.error) {
        const errors = [];
        if (uiField.error.isInvalidValue) {
            errors.push(t('Invalid value'));
        }
        if (uiField.error.isLengthTooLong) {
            const errorDetails = uiField.mapper?.maxLength &&
                t('Maximum {{ maxLength }} characters allowed.', {
                    maxLength: uiField.mapper.maxLength
                });
            errors.push(t('Value is too long.'), errorDetails);
        }
        if (uiField.error.isMissingRequired) {
            errors.push(t('Value is required'));
        }
        error = errors.join(' ');
    }
    const isSelect = uiField.uiFieldType === 'select' || uiField.uiFieldType === 'multiselect';
    React.useEffect(() => {
        if (!error) {
            return;
        }
        if (isSelect) {
            selectRef.current?.focus();
        }
        else {
            inputRef.current?.focus();
        }
    }, [error, isSelect]);
    if (isSelect) {
        return (jsxRuntime.jsx(cauldronReact.Select, { label: uiField.name, required: isRequired, requiredText: isRequired ? t('Required') : '', "data-testid": uiField.key, error: error, ref: selectRef, value: uiField.value, onChange: ({ target }) => {
                uiField.value = target.value;
                setUIField({ ...uiField });
            }, options: [
                {
                    key: 'choose',
                    label: t('Please choose'),
                    value: ''
                },
                ...(uiField.allowedValues?.map(v => ({
                    key: v.id,
                    label: v.name,
                    value: v.id
                })) ?? [])
            ] }, uiField.key));
    }
    if (uiField.uiFieldType === 'async-select') {
        return (jsxRuntime.jsx(SelectParentIssueComponent, { selectedTemplate: selectedTemplate, uiField: uiField, setUIField: setUIField }));
    }
    return (jsxRuntime.jsx(cauldronReact.TextField, { "data-testid": uiField.key, required: isRequired, type: uiField.uiFieldType, error: error, fieldRef: inputRef, label: uiField.name, value: uiField.value, onChange: newValue => {
            uiField.value = newValue;
            setUIField({ ...uiField });
        } }, uiField.key));
};

const ProvideValues = ({ uiFields, setUIFields, selectedTemplate }) => {
    const { t } = useTranslation();
    if (!uiFields) {
        return jsxRuntime.jsx("div", { children: t('Error') });
    }
    return (jsxRuntime.jsxs("div", { children: [uiFields.requiredFields.map((uiField, index) => {
                return (jsxRuntime.jsx(ProvideValuesComponent, { selectedTemplate: selectedTemplate, isRequired: true, uiField: uiField, setUIField: (updatedUIField) => {
                        uiFields.requiredFields[index] = updatedUIField;
                        setUIFields({ ...uiFields });
                    } }, uiField.key));
            }), jsxRuntime.jsxs(cauldronReact.Accordion, { children: [jsxRuntime.jsx(cauldronReact.AccordionTrigger, { children: t('All Other Fields') }), jsxRuntime.jsx(cauldronReact.AccordionContent, { children: uiFields.nonRequiredFields.map((uiField, index) => {
                            return (jsxRuntime.jsx(ProvideValuesComponent, { selectedTemplate: selectedTemplate, uiField: uiField, isRequired: false, setUIField: (updatedUIField) => {
                                    uiFields.nonRequiredFields[index] = updatedUIField;
                                    setUIFields({ ...uiFields });
                                } }, uiField.key));
                        }) })] })] }));
};

const useIntegrationProjectsFromTemplates = ({ activeConnections, enterpriseId, apiHostname, authFn, setConnectionIdsWithLoadingErrors }) => {
    const { t } = useTranslation();
    const [isProjectsLoading, setIsProjectsLoading] = React.useState(false);
    const [projectsByConnections, setProjectsByConnections] = React.useState({});
    const [projectsError, setProjectsError] = React.useState(null);
    React.useEffect(() => {
        if (!activeConnections.length) {
            return;
        }
        const fetchProjects = async () => {
            setIsProjectsLoading(true);
            setConnectionIdsWithLoadingErrors([]);
            try {
                const token = await authFn();
                const projectsConnectionsMap = {};
                const connectionsWithLoadingErrors = [];
                for (const { id, templates } of activeConnections) {
                    const usedProjectsFromTemplatesSet = new Set(templates?.map(temp => temp.project_id));
                    if (!templates?.length || !usedProjectsFromTemplatesSet.size) {
                        continue;
                    }
                    try {
                        const connectionProjects = await getProjectsAndIssueTypes({
                            apiHostname,
                            token,
                            enterpriseId,
                            connectionId: id
                        });
                        const templatesProjects = connectionProjects?.filter(pr => usedProjectsFromTemplatesSet.has(pr.id));
                        projectsConnectionsMap[id] = templatesProjects;
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    }
                    catch (connectionError) {
                        connectionsWithLoadingErrors.push(id);
                        // continue loading other connections projects even if some connections failed to load projects
                        continue;
                    }
                }
                setProjectsByConnections(projectsConnectionsMap);
                if (connectionsWithLoadingErrors.length) {
                    setConnectionIdsWithLoadingErrors(connectionsWithLoadingErrors);
                    throw new Error(t('Some connections failed to load projects'));
                }
            }
            catch (error) {
                setProjectsError(error);
            }
            finally {
                setIsProjectsLoading(false);
            }
        };
        fetchProjects();
    }, [activeConnections, enterpriseId]);
    return {
        projectsByConnections,
        isProjectsLoading,
        projectsError
    };
};

const useSendToIntegrationModalSteps = ({ selectedIntegrationData, apiHostname, authFn, userEnterpriseId, selectedTemplate, setSelectedTemplate, uiFields, setUIFields, issues }) => {
    const { t } = useTranslation();
    const [templateError, setTemplateError] = React.useState('');
    const [connectionIdsWithLoadingErrors, setConnectionIdsWithLoadingErrors] = React.useState([]);
    const [provideValuesStepError, setProvideValuesStepError] = React.useState('');
    const { isProjectsLoading, projectsByConnections, projectsError } = useIntegrationProjectsFromTemplates({
        activeConnections: selectedIntegrationData?.activeConnections || [],
        enterpriseId: userEnterpriseId,
        apiHostname,
        authFn,
        setConnectionIdsWithLoadingErrors
    });
    const isSelectTemplateStepValid = React.useCallback(() => {
        if (!selectedTemplate) {
            setTemplateError(t('Please select a template'));
            return false;
        }
        setTemplateError('');
        return true;
    }, [selectedTemplate]);
    React.useEffect(() => {
        if (!selectedTemplate) {
            return;
        }
        const preparedUiFields = prepareUIFields(selectedTemplate, issues[0]);
        setUIFields(preparedUiFields);
    }, [selectedTemplate, issues[0]]);
    const isProvideValuesStepValid = React.useCallback(() => {
        if (!uiFields) {
            return false;
        }
        let hasAnError = false;
        for (const uiField of uiFields.requiredFields) {
            const { integrationValue, error: integrationError } = uiField.mapper.convertDisplayToIntegrationValue(uiField);
            if (integrationError) {
                hasAnError = true;
                uiField.error = integrationError;
            }
            else {
                uiField.integrationValue = integrationValue;
                uiField.error = undefined;
            }
        }
        for (const uiField of uiFields.nonRequiredFields) {
            const { integrationValue, error: integrationError } = uiField.mapper.convertDisplayToIntegrationValue(uiField);
            if (integrationError) {
                hasAnError = true;
                uiField.error = integrationError;
            }
            else {
                uiField.integrationValue = integrationValue;
                uiField.error = undefined;
            }
        }
        setUIFields({ ...uiFields });
        return !hasAnError;
    }, [uiFields]);
    React.useEffect(() => {
        if (!selectedTemplate) {
            return;
        }
        const missedFields = getMissedRequiredFields(selectedTemplate);
        if (!missedFields.length) {
            setProvideValuesStepError('');
            return;
        }
        const project = projectsByConnections[selectedTemplate.connection.id]?.find(({ id }) => id === selectedTemplate.project_id);
        const issueType = project?.issueTypes?.find(({ id }) => id === selectedTemplate.issue_type_id);
        const projectName = project?.name || '';
        const issueTypeName = issueType?.name || '';
        const message = t(`Mapped Field {{fieldNames}} is not available for issue type "{{issueTypeName}}" in project "{{projectName}}". To resolve this issue, please add the {{fieldNames}} field to the create screen for "{{issueTypeName}}" in your {{integration}} configuration settings.`, {
            fieldNames: missedFields.map(name => `"${name}"`).join(', '),
            integration: selectedIntegrationData?.productName,
            issueTypeName,
            projectName
        });
        setProvideValuesStepError(message);
    }, [selectedTemplate, t]);
    const steps = selectedIntegrationData
        ? [
            {
                isValid: isSelectTemplateStepValid,
                heading: t('Choose template'),
                content: (jsxRuntime.jsx(SelectTemplate, { multiple: selectedIntegrationData.isMultipleIssues, hostProductName: apiHostname, integrationData: selectedIntegrationData, selectedTemplate: selectedTemplate, setSelectedTemplate: setSelectedTemplate, projectsByConnections: projectsByConnections, connectionIdsWithLoadingErrors: connectionIdsWithLoadingErrors, loading: isProjectsLoading, loadingError: !!projectsError, allConnectionsTemplates: selectedIntegrationData.allConnectionsTemplates, templateError: templateError }))
            },
            {
                isValid: isProvideValuesStepValid,
                heading: t('Set values'),
                content: (jsxRuntime.jsx(ProvideValues, { uiFields: uiFields, setUIFields: setUIFields, selectedTemplate: selectedTemplate }))
            }
        ]
        : [];
    return {
        steps,
        isProjectsLoading,
        projectsByConnections,
        projectsError,
        connectionIdsWithLoadingErrors,
        provideValuesStepError
    };
};

const unavailableInAxeMonitorFields = [
    'screenshotURL',
    'tags',
    'testName',
    'shareURL',
    'testUrl',
    'foundBy',
    'ruleId'
];
const unavailableFieldsByProducts = {
    [billingUtils.ProductSlugs.axeMonitor]: unavailableInAxeMonitorFields
};
const hasTemplateCompatibilityIssues = (template, sourceProduct) => {
    const unavailableFields = unavailableFieldsByProducts[sourceProduct];
    if (!template || !template.fieldMappings?.mapping || !unavailableFields) {
        return false;
    }
    const mappedFields = Object.values(template.fieldMappings.mapping).map(({ source }) => source);
    return mappedFields.some(field => unavailableFields.includes(field));
};
const useSendToIntegrationModal = ({ selectedIntegrationData, apiHostname, authFn, userEnterpriseId, closeModal, onSuccess, onError, issues, hostProduct }) => {
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = React.useState(false);
    const [sendIssueError, setSendIssueError] = React.useState();
    const [selectedTemplate, setSelectedTemplate] = React.useState();
    const [uiFields, setUIFields] = React.useState();
    const { steps, isProjectsLoading, projectsByConnections, projectsError, connectionIdsWithLoadingErrors, provideValuesStepError } = useSendToIntegrationModalSteps({
        selectedIntegrationData,
        issues,
        apiHostname,
        authFn,
        userEnterpriseId,
        selectedTemplate,
        setSelectedTemplate,
        uiFields,
        setUIFields
    });
    const hasCompatibilityIssues = React.useMemo(() => selectedTemplate &&
        hasTemplateCompatibilityIssues(selectedTemplate, hostProduct.slug), [selectedTemplate, hostProduct.slug]);
    const onModalSubmit = async () => {
        if (!selectedIntegrationData ||
            !uiFields ||
            isSaving ||
            !selectedTemplate) {
            return;
        }
        const fieldData = prepareUIFieldsForSubmission(uiFields);
        const body = {
            template_id: selectedTemplate.templateId,
            group_id: issues[0].groupId || '',
            product_source_slug: hostProduct.slug,
            issues: issues,
            mappedIntegrationFields: Object.entries(fieldData).map(([key, value]) => ({ key, value }))
        };
        const sendIssueData = {
            integrationProductSlug: selectedIntegrationData.productSlug,
            project: selectedTemplate.project_id,
            issueType: selectedTemplate.issue_type_id,
            template: selectedTemplate.templateId,
            isMultipleShare: selectedIntegrationData.isMultipleIssues
        };
        try {
            setIsSaving(true);
            const token = await authFn();
            await sendIssue({
                apiHostname: apiHostname,
                token,
                enterpriseId: userEnterpriseId,
                connectionId: selectedTemplate.connection.id,
                body
            });
            setIsSaving(false);
            onSuccess(sendIssueData);
            closeModal();
        }
        catch (error) {
            const e = error;
            const { productName, isMultipleIssues } = selectedIntegrationData;
            const errorMessage = isMultipleIssues
                ? t('Error with sending all issues to {{ productName }}.', {
                    productName
                })
                : t('Error with sending issue to {{ productName }}.', {
                    productName
                });
            const { errorFields, errorId } = e?.details || {};
            const errorFieldsMessage = errorFields?.join(' ');
            const errorIdMessage = errorId
                ? t('Error ID: {{errorId}}.', { errorId })
                : '';
            const combinedErrorMessage = [
                errorMessage,
                errorFieldsMessage,
                errorIdMessage
            ]
                .filter(Boolean)
                .join(' ');
            setSendIssueError(combinedErrorMessage);
            setIsSaving(false);
            onError?.(sendIssueData);
        }
    };
    return {
        steps,
        onModalSubmit,
        isProjectsLoading,
        projectsByConnections,
        projectsError,
        isSaving,
        sendIssueError,
        setSendIssueError,
        connectionIdsWithLoadingErrors,
        hasCompatibilityIssues,
        provideValuesStepError
    };
};

var modules_6bfab05e = {"stepperModal":"_stepperModal_5a85fb80","dialogMainContent":"_dialogMainContent_5a85fb80","stepper":"_stepper_5a85fb80","chooseTemplateErrorToast":"_chooseTemplateErrorToast_5a85fb80","chooseTemplateWarningToast":"_chooseTemplateWarningToast_5a85fb80","modalFooter":"_modalFooter_5a85fb80"};

const getStepStatus = (currentStep, stepNumber) => currentStep === stepNumber
    ? 'current'
    : currentStep > stepNumber
        ? 'complete'
        : 'future';
const getOffscreenText = (stepStatus, t) => stepStatus === 'complete' ? (jsxRuntime.jsx(cauldronReact.Offscreen, { children: t('Completed step') })) : stepStatus === 'future' ? (jsxRuntime.jsx(cauldronReact.Offscreen, { children: t('Future step') })) : (jsxRuntime.jsx(cauldronReact.Offscreen, { children: t('Current step') }));
const SendToIntegrationModal = ({ closeFn, issues, hostProduct, ...props }) => {
    const { t } = useTranslation();
    const headingRef = React.useRef(null);
    const loaderRef = React.useRef(null);
    const { steps, onModalSubmit, isSaving, sendIssueError, setSendIssueError, connectionIdsWithLoadingErrors, hasCompatibilityIssues, provideValuesStepError } = useSendToIntegrationModal({
        ...props,
        closeModal: closeFn,
        issues,
        hostProduct
    });
    const [currentStep, setCurrentStep] = React.useState(0);
    cauldronReact.useDidUpdate(() => {
        headingRef.current?.focus();
    }, [currentStep]);
    cauldronReact.useDidUpdate(() => {
        loaderRef.current?.focus();
    }, [isSaving]);
    const isLastStep = currentStep === steps.length - 1;
    const isFirstStep = currentStep === 0;
    const incrementStep = React.useCallback(() => setCurrentStep(s => s + 1), []);
    const onBackClick = React.useCallback(() => setCurrentStep(s => s - 1), []);
    const onContinueClick = () => {
        const isValid = steps[currentStep].isValid();
        if (!isValid) {
            return;
        }
        incrementStep();
    };
    const onSaveClick = async () => {
        const isValid = steps[currentStep].isValid();
        if (!isValid) {
            return;
        }
        await onModalSubmit();
    };
    const isLoading = isSaving;
    const stepperModalHeadingId = `stepper-modal-heading-${utils.useId()}`;
    const integrationName = props.selectedIntegrationData.productName;
    const { isMultipleIssues } = props.selectedIntegrationData;
    return (jsxRuntime.jsx(ApiConfigProvider, { apiHostname: props.apiHostname, authFn: props.authFn, userEnterpriseId: props.userEnterpriseId, hostProduct: hostProduct, children: jsxRuntime.jsxs(cauldronReact.Modal, { show: true, className: modules_6bfab05e.stepperModal, "aria-labelledby": stepperModalHeadingId, heading: jsxRuntime.jsx("span", { id: stepperModalHeadingId, children: isMultipleIssues
                    ? t('Share all to {{ integrationName }}', { integrationName })
                    : t('Share to {{ integrationName }}', { integrationName }) }), onClose: closeFn, children: [!!sendIssueError && (jsxRuntime.jsx(cauldronReact.Toast, { show: true, onDismiss: () => setSendIssueError(''), type: "error", className: modules_6bfab05e.chooseTemplateErrorToast, children: sendIssueError })), !!connectionIdsWithLoadingErrors.length && isFirstStep && (jsxRuntime.jsx(cauldronReact.Toast, { show: true, dismissible: false, type: "caution", className: modules_6bfab05e.chooseTemplateWarningToast, children: t('Failed to load connection data for some connections') })), hasCompatibilityIssues && isFirstStep && (jsxRuntime.jsx(cauldronReact.Toast, { show: true, dismissible: false, type: "caution", className: modules_6bfab05e.chooseTemplateWarningToast, children: t('This template includes fields not available in {{name}}. Values for these fields can be set manually.', { name: hostProduct.name }) })), !!provideValuesStepError && (jsxRuntime.jsx(cauldronReact.Toast, { show: true, type: "error", dismissible: false, children: provideValuesStepError })), jsxRuntime.jsx(cauldronReact.Stepper, { className: modules_6bfab05e.stepper, children: steps.map((step, i) => (jsxRuntime.jsxs(cauldronReact.Step, { status: getStepStatus(currentStep, i), children: [step.heading, ' ', getOffscreenText(getStepStatus(currentStep, i), t)] }, step.heading))) }), jsxRuntime.jsx(cauldronReact.ModalContent, { children: isLoading ? (jsxRuntime.jsx(cauldronReact.Loader, { label: t('Loading...'), ref: loaderRef })) : (jsxRuntime.jsxs("div", { className: modules_6bfab05e.dialogMainContent, children: [jsxRuntime.jsx("h3", { ref: headingRef, tabIndex: -1, children: steps[currentStep].heading }), steps[currentStep].content] })) }), jsxRuntime.jsxs(cauldronReact.ModalFooter, { className: modules_6bfab05e.modalFooter, children: [isFirstStep ? (jsxRuntime.jsx(cauldronReact.Button, { variant: "secondary", onClick: closeFn, children: t('Cancel') })) : (jsxRuntime.jsx(cauldronReact.Button, { variant: "secondary", onClick: onBackClick, disabled: isLoading, children: t('Back') })), isLastStep ? (jsxRuntime.jsx(cauldronReact.Button, { onClick: onSaveClick, disabled: isLoading || !!provideValuesStepError, children: t('Save') })) : (jsxRuntime.jsx(cauldronReact.Button, { onClick: onContinueClick, disabled: isLoading || !!provideValuesStepError, children: t('Continue') }))] })] }) }));
};

const SendToIntegrationButtons = ({ productSlug, canViewIssue, sentIssueLinks, alreadySentForMultiple, isMultipleIssues, productName, canSendIssue, isIssueAlreadySentToSomeIntegration, onShareButtonClick, onInitiateSendIssue }) => {
    const { t } = useTranslation();
    const integration = productName.toLocaleUpperCase();
    return (jsxRuntime.jsxs(React.Fragment, { children: [canViewIssue &&
                sentIssueLinks.map(sentIssueLink => (jsxRuntime.jsx(cauldronReact.Button, { thin: true, variant: "secondary", onClick: () => {
                        window.open(sentIssueLink, '_blank', 'noopener,noreferrer');
                    }, children: t('OPEN IN {{ integration }}', { integration }) }, sentIssueLink))), alreadySentForMultiple && (jsxRuntime.jsx(cauldronReact.TooltipTabstop, { tooltip: t('1 or more of the issues below have already been shared to {{ integration }}', { integration: productName }), children: jsxRuntime.jsxs(cauldronReact.Button, { thin: true, variant: "secondary", disabled: true, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "share-nodes" }), isMultipleIssues
                            ? t('SHARE ALL TO {{ integration }}', { integration })
                            : t('SHARE TO {{ integration }}', { integration })] }) })), canSendIssue && !isIssueAlreadySentToSomeIntegration && (jsxRuntime.jsxs(cauldronReact.Button, { thin: true, variant: "secondary", onClick: () => {
                    onShareButtonClick();
                    onInitiateSendIssue?.({
                        integrationProductSlug: productSlug,
                        isMultipleShare: isMultipleIssues
                    });
                }, children: [jsxRuntime.jsx(cauldronReact.Icon, { type: "share-nodes" }), isMultipleIssues
                        ? t('SHARE ALL TO {{ integration }}', { integration })
                        : t('SHARE TO {{ integration }}', { integration })] }))] }));
};
const SendToIntegrationContainer = ({ apiHostname, authFn, userEnterpriseId, selectedIntegrationData, sendToIntegrationContainerData, onModalClose, onSendIssueSuccess, setsSelectedIntegrationData, onInitiateSendIssue }) => {
    const { integrationData, issues } = sendToIntegrationContainerData;
    // temporary solution to check if the issue is already sent to some integration to save previous implementation
    // TODO: remove this once when we allow to send the same issue to multiple integrations
    const isIssueAlreadySentToSomeIntegration = React.useMemo(() => !!issues[0]?.integrationUrl, [issues]);
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [selectedIntegrationData && (jsxRuntime.jsx(SendToIntegrationModal, { selectedIntegrationData: selectedIntegrationData, issues: issues, hostProduct: sendToIntegrationContainerData.hostProduct, apiHostname: apiHostname, authFn: authFn, userEnterpriseId: userEnterpriseId, closeFn: onModalClose, onSuccess: onSendIssueSuccess })), integrationData.map(integrationConfig => (jsxRuntime.jsx(SendToIntegrationButtons, { ...integrationConfig, isIssueAlreadySentToSomeIntegration: isIssueAlreadySentToSomeIntegration, onShareButtonClick: () => setsSelectedIntegrationData(integrationConfig), onInitiateSendIssue: onInitiateSendIssue }, integrationConfig.productSlug)))] }));
};

function getIntegrationProductNameFromSlug(integrationProductSlug) {
    switch (integrationProductSlug) {
        case billingUtils.ProductSlugs.jiraDirectCloudIntegration:
            return 'Jira Cloud';
        case billingUtils.ProductSlugs.jiraOnPremIntegration:
            return 'Jira Data Center';
        case billingUtils.ProductSlugs.customIntegration:
            return 'Custom Integration';
        default:
            throw new Error(`Unsupported integration product slug ${integrationProductSlug}`);
    }
}

const ACTIVE_STATES = ['paid', 'trialing'];

const handleAsyncState = async (callback, setLoading, setError) => {
    setLoading(true);
    setError(null);
    try {
        await callback();
    }
    catch (error) {
        setError(error);
    }
    finally {
        setLoading(false);
    }
};

const DEFAULT_SUPPORTED_INTEGRATION_SLUGS = [
    billingUtils.ProductSlugs.jiraDirectCloudIntegration,
    billingUtils.ProductSlugs.jiraOnPremIntegration
];
const isActiveState = (state) => ACTIVE_STATES.includes(state);
const useGetAllowedIntegrations = (sendToIntegrationProps) => {
    const [integrationsLoading, setIntegrationsLoading] = React.useState(false);
    const [integrationsError, setIntegrationsError] = React.useState(null);
    const supportedIntegrationSlugs = sendToIntegrationProps.supportedIntegrationSlugs
        ? sendToIntegrationProps.supportedIntegrationSlugs
        : DEFAULT_SUPPORTED_INTEGRATION_SLUGS;
    const [loadedSubscriptionsSlugs, setLoadedSubscriptionsSlugs] = React.useState([]);
    const [allowedIntegrations, setAllowedIntegrations] = React.useState([]);
    React.useEffect(() => {
        if (!sendToIntegrationProps.userEnterpriseId ||
            !sendToIntegrationProps.userId) {
            return;
        }
        async function loadSubscriptionsSlags() {
            if (sendToIntegrationProps.subscriptionsFn) {
                const subscriptionsSlugs = await sendToIntegrationProps.subscriptionsFn();
                setLoadedSubscriptionsSlugs(subscriptionsSlugs);
                setAllowedIntegrations(supportedIntegrationSlugs.filter(supportedIntegrationSlug => subscriptionsSlugs.includes(supportedIntegrationSlug)));
                return;
            }
            const bearerToken = await sendToIntegrationProps.authFn();
            const enterprise = await getEnterpriseById({
                apiHostname: sendToIntegrationProps.apiHostname,
                token: bearerToken,
                enterpriseId: sendToIntegrationProps.userEnterpriseId
            });
            const enterpriseMembers = await getEnterpriseMemberById({
                apiHostname: sendToIntegrationProps.apiHostname,
                token: bearerToken,
                enterpriseId: sendToIntegrationProps.userEnterpriseId
            });
            const memberProductAccess = enterpriseMembers.find(({ user_id }) => user_id === sendToIntegrationProps.userId)?.product_access ?? [];
            // Intersect active enterprise subscriptions with member product access
            // for each integration's direct slug and bundle provider slugs.
            const allowedIntegrationSlugs = supportedIntegrationSlugs.filter(supportedIntegrationSlug => {
                const providerSlugs = billingUtils.getAccessProviderSlugs(supportedIntegrationSlug);
                const activeEnterpriseSubscriptionSlugs = enterprise.subscriptions
                    .filter(({ product_slug, purchase_state }) => providerSlugs.includes(product_slug) &&
                    isActiveState(purchase_state))
                    .map(({ product_slug }) => product_slug);
                return memberProductAccess.some(({ product_slug }) => activeEnterpriseSubscriptionSlugs.includes(product_slug));
            });
            setLoadedSubscriptionsSlugs(allowedIntegrationSlugs);
            setAllowedIntegrations(allowedIntegrationSlugs);
        }
        handleAsyncState(loadSubscriptionsSlags, setIntegrationsLoading, setIntegrationsError);
    }, [sendToIntegrationProps.userEnterpriseId]);
    return {
        allowedIntegrations,
        loadedSubscriptionsSlugs,
        integrationsLoading,
        integrationsError
    };
};

const useGetConnectedConnections = (sendToIntegrationProps) => {
    const [connectedConnectionsLoading, setConnectedConnectionsLoading] = React.useState(false);
    const [connectedConnectionsError, setConnectedConnectionsError] = React.useState(null);
    const [connectedConnections, setConnectedConnections] = React.useState([]);
    React.useEffect(() => {
        if (!sendToIntegrationProps.userEnterpriseId) {
            return;
        }
        async function loadConnectedConnections() {
            const bearerToken = await sendToIntegrationProps.authFn();
            const allIntegrationConnections = await getIntegrationConnections({
                apiHostname: sendToIntegrationProps.apiHostname,
                token: bearerToken,
                enterpriseId: sendToIntegrationProps.userEnterpriseId
            });
            // Filter to only have connected connections and create a map of connections, for the faster search
            const integrationConnections = allIntegrationConnections.filter(connection => connection.status === 'connected');
            setConnectedConnections(integrationConnections);
        }
        handleAsyncState(loadConnectedConnections, setConnectedConnectionsLoading, setConnectedConnectionsError);
    }, [sendToIntegrationProps.userEnterpriseId]);
    return {
        connectedConnections,
        connectedConnectionsLoading,
        connectedConnectionsError
    };
};

const useGetConnectionsTemplates = (sendToIntegrationProps, connectedConnections) => {
    const [templatesByConnections, setTemplatesByConnections] = React.useState({});
    const [templatesLoading, setTemplatesLoading] = React.useState(false);
    const [templatesError, setTemplatesError] = React.useState(null);
    React.useEffect(() => {
        if (!sendToIntegrationProps.userEnterpriseId ||
            !connectedConnections.length) {
            return;
        }
        async function loadTemplates() {
            const bearerToken = await sendToIntegrationProps.authFn();
            // Load all templates for the enterpise
            const enterpriseTemplates = await getTemplates({
                apiHostname: sendToIntegrationProps.apiHostname,
                token: bearerToken,
                enterpriseId: sendToIntegrationProps.userEnterpriseId
            });
            const templates = {};
            // Add templates into connections
            for (const connectionTemplate of enterpriseTemplates) {
                const connectionId = connectionTemplate.integration_connection_id;
                const activeConnection = connectedConnections.find(({ id }) => id === connectionId);
                if (activeConnection) {
                    templates[connectionId] = templates[connectionId] || [];
                    templates[connectionId].push({
                        templateId: connectionTemplate.id,
                        project_id: connectionTemplate.project_id,
                        issue_type_id: connectionTemplate.issue_type_id,
                        name: connectionTemplate.name,
                        fieldMappings: connectionTemplate.field_mappings,
                        impactMappings: connectionTemplate.impact_mappings,
                        summary_prefix: connectionTemplate.summary_prefix,
                        connection: {
                            ...activeConnection,
                            baseUrl: activeConnection.base_url || '',
                            templates: [],
                            productSlug: activeConnection.product_slug
                        }
                    });
                }
            }
            setTemplatesByConnections(templates);
        }
        handleAsyncState(loadTemplates, setTemplatesLoading, setTemplatesError);
    }, [connectedConnections, sendToIntegrationProps.userEnterpriseId]);
    return { templatesByConnections, templatesLoading, templatesError };
};

const fetchIssuesMetadataByChunk = async (issueIds, authFn, apiHostname, enterpriseId) => {
    const bearerToken = await authFn();
    const chunkSize = 50;
    const issueIdsChunks = [];
    for (let i = 0; i < issueIds.length; i += chunkSize) {
        issueIdsChunks.push(issueIds.slice(i, i + chunkSize));
    }
    const requestedIssuesMetadata = (await Promise.all(issueIdsChunks.map(chunk => getSentIssuesByIds({
        apiHostname,
        token: bearerToken,
        enterpriseId,
        issueIds: chunk
    })))).flat();
    return requestedIssuesMetadata;
};
const useGetIssuesData = ({ userEnterpriseId, issuesFn, issueIDExtractorFn, authFn, apiHostname }) => {
    const [issues, setIssues] = React.useState([]);
    const [issuesIDs, setIssuesIDs] = React.useState([]);
    const [issuesLoading, setIssuesLoading] = React.useState(false);
    const [issuesError, setIssuesError] = React.useState(null);
    const [sentIssues, setSentIssues] = React.useState([]);
    React.useEffect(() => {
        if (!userEnterpriseId) {
            return;
        }
        async function loadIssues() {
            const allIssues = await issuesFn();
            const issuesWithIds = allIssues[0].issueId
                ? allIssues
                : allIssues.map(issueEntity => ({
                    ...issueEntity,
                    issueId: issueIDExtractorFn(issueEntity)
                }));
            const issuesIDList = allIssues.map(issue => issueIDExtractorFn(issue));
            setIssues(issuesWithIds);
            setIssuesIDs(issuesIDList);
            if (!issuesIDList.length) {
                return;
            }
            const requestedIssuesMetadata = await fetchIssuesMetadataByChunk(issuesIDList, authFn, apiHostname, userEnterpriseId);
            setSentIssues(requestedIssuesMetadata);
        }
        handleAsyncState(loadIssues, setIssuesLoading, setIssuesError);
    }, [issuesFn, userEnterpriseId]);
    const refetchIssues = React.useCallback(async () => {
        try {
            setIssuesLoading(true);
            const requestedIssuesMetadata = await fetchIssuesMetadataByChunk(issuesIDs, authFn, apiHostname, userEnterpriseId);
            setSentIssues(requestedIssuesMetadata);
        }
        catch (error) {
            setIssuesError(error);
        }
        finally {
            setIssuesLoading(false);
        }
    }, [issuesIDs, authFn, apiHostname, userEnterpriseId]);
    return {
        issues,
        issuesIDs,
        sentIssues,
        issuesLoading,
        issuesError,
        refetchIssues
    };
};

const useSendToIntegrationContainerData = (sendToIntegrationProps) => {
    const { allowedIntegrations, integrationsLoading, integrationsError } = useGetAllowedIntegrations(sendToIntegrationProps);
    const { connectedConnections, connectedConnectionsLoading, connectedConnectionsError } = useGetConnectedConnections(sendToIntegrationProps);
    const { templatesByConnections, templatesLoading, templatesError } = useGetConnectionsTemplates(sendToIntegrationProps, connectedConnections);
    const { issues, issuesIDs, sentIssues, issuesLoading, issuesError, refetchIssues } = useGetIssuesData(sendToIntegrationProps);
    const [integrationData, setIntegrationData] = React.useState([]);
    const [bearerToken, setBearerToken] = React.useState('');
    React.useEffect(() => {
        if (!allowedIntegrations.length ||
            !connectedConnections.length ||
            !Object.keys(templatesByConnections).length ||
            issuesLoading) {
            return;
        }
        const integrationDataList = allowedIntegrations.map(integration => {
            const connectionsByIntegration = connectedConnections.filter(connection => connection.product_slug === integration);
            const connectionsByIntegrationIds = connectionsByIntegration.map(connection => connection.id);
            const templates = connectionsByIntegration
                .map(({ id }) => templatesByConnections[id] || [])
                .flat();
            const sentIssueLinks = sentIssues
                .filter(issue => connectionsByIntegrationIds.includes(issue.integration_connection_id))
                .map(issue => issue.integration_url);
            const integrationDataItem = {
                productSlug: integration,
                isMultipleIssues: issues.length > 1,
                productName: getIntegrationProductNameFromSlug(integration),
                canSendIssue: !!templates.length && !sentIssueLinks.length,
                alreadySentForMultiple: issues.length > 1 && !!sentIssueLinks.length,
                canViewIssue: issues.length <= 1 && !!sentIssueLinks.length,
                sentIssueLinks,
                activeConnections: connectionsByIntegration.map(connection => ({
                    id: connection.id,
                    baseUrl: connection.base_url || '',
                    templates: templatesByConnections[connection.id],
                    productSlug: connection.product_slug
                })),
                allConnectionsTemplates: templates
            };
            return integrationDataItem;
        });
        setIntegrationData(integrationDataList);
    }, [
        allowedIntegrations,
        connectedConnections,
        issues,
        sentIssues,
        templatesByConnections,
        issuesLoading
    ]);
    React.useEffect(() => {
        async function loadBearerToken() {
            const token = await sendToIntegrationProps.authFn();
            setBearerToken(token);
        }
        loadBearerToken();
    }, []);
    const loading = connectedConnectionsLoading ||
        templatesLoading ||
        issuesLoading ||
        integrationsLoading;
    const error = connectedConnectionsError ||
        templatesError ||
        issuesError ||
        integrationsError;
    const sendToIntegrationContainerData = {
        hostProduct: sendToIntegrationProps.hostProduct,
        bearerToken,
        issuesID: issuesIDs,
        issues,
        integrationData
    };
    return {
        sendToIntegrationContainerData,
        loading,
        error,
        refetchIssues
    };
};

const useSendToIntegrationLogic = (sendToIntegrationProps) => {
    const [selectedIntegrationData, setsSelectedIntegrationData] = React.useState(null);
    const { sendToIntegrationContainerData, loading, error, refetchIssues } = useSendToIntegrationContainerData(sendToIntegrationProps);
    const onModalClose = React.useCallback(() => setsSelectedIntegrationData(null), []);
    const onSendIssueSuccess = React.useCallback((args) => {
        refetchIssues();
        setsSelectedIntegrationData(null);
        sendToIntegrationProps.onSuccessSendIssue?.(args);
    }, [refetchIssues]);
    const props = React.useMemo(() => ({
        apiHostname: sendToIntegrationProps.apiHostname,
        authFn: sendToIntegrationProps.authFn,
        userEnterpriseId: sendToIntegrationProps.userEnterpriseId,
        selectedIntegrationData,
        sendToIntegrationContainerData,
        onModalClose,
        onSendIssueSuccess,
        onInitiateSendIssue: sendToIntegrationProps.onInitiateSendIssue,
        setsSelectedIntegrationData
    }), [
        sendToIntegrationProps.apiHostname,
        sendToIntegrationProps.authFn,
        sendToIntegrationProps.userEnterpriseId,
        selectedIntegrationData,
        sendToIntegrationContainerData,
        onModalClose,
        onSendIssueSuccess,
        sendToIntegrationProps.onInitiateSendIssue
    ]);
    return {
        loading,
        error,
        props
    };
};

const supportedLocales = ['en', 'fr', 'it', 'de', 'es', 'ja', 'fi'];

const useIntegrationsConfiguration = (config) => {
    const { allowedIntegrations, integrationsLoading, integrationsError } = useGetAllowedIntegrations(config);
    const { connectedConnections, connectedConnectionsLoading, connectedConnectionsError } = useGetConnectedConnections(config);
    const { templatesByConnections, templatesLoading, templatesError } = useGetConnectionsTemplates(config, connectedConnections);
    const loading = connectedConnectionsLoading || templatesLoading || integrationsLoading;
    const error = connectedConnectionsError || templatesError || integrationsError;
    const integrationsConfiguration = React.useMemo(() => {
        if (!allowedIntegrations.length ||
            !connectedConnections.length ||
            !Object.keys(templatesByConnections).length) {
            return [];
        }
        const configuration = allowedIntegrations.map(integration => {
            const connectionsByIntegration = connectedConnections.filter(({ product_slug }) => product_slug === integration);
            const templates = connectionsByIntegration
                .map(({ id }) => templatesByConnections[id] || [])
                .flat();
            const activeConnections = connectionsByIntegration.map(connection => ({
                id: connection.id,
                baseUrl: connection.base_url || '',
                templates: templatesByConnections[connection.id],
                productSlug: connection.product_slug
            }));
            return {
                productSlug: integration,
                productName: getIntegrationProductNameFromSlug(integration),
                canSendIssue: !!templates.length,
                allConnectionsTemplates: templates,
                activeConnections
            };
        });
        return configuration;
    }, [allowedIntegrations, connectedConnections, templatesByConnections]);
    return {
        integrationsConfiguration,
        integrationsConfigurationLoading: loading,
        integrationsConfigurationError: error
    };
};

const useGetIssuesMetadata = ({ userEnterpriseId, issuesFn, issueIDExtractorFn, authFn, apiHostname }) => {
    const [issuesIDs, setIssuesIDs] = React.useState([]);
    const [issuesMetadataLoading, setIssuesMetadataLoading] = React.useState(false);
    const [issuesMetadataError, setIssuesMetadataError] = React.useState(null);
    const [issuesMetadata, setIssuesMetadata] = React.useState([]);
    React.useEffect(() => {
        if (!userEnterpriseId) {
            return;
        }
        async function loadIssuesMetadata() {
            const allIssues = await issuesFn();
            const issuesIDList = allIssues.map(issue => issueIDExtractorFn(issue));
            setIssuesIDs(issuesIDList);
            if (!issuesIDList.length) {
                return;
            }
            const requestedIssuesMetadata = await fetchIssuesMetadataByChunk(issuesIDList, authFn, apiHostname, userEnterpriseId);
            setIssuesMetadata(requestedIssuesMetadata);
        }
        handleAsyncState(loadIssuesMetadata, setIssuesMetadataLoading, setIssuesMetadataError);
    }, [issuesFn, userEnterpriseId]);
    const refetchIssuesMetadata = React.useCallback(() => handleAsyncState(async () => {
        const requestedIssuesMetadata = await fetchIssuesMetadataByChunk(issuesIDs, authFn, apiHostname, userEnterpriseId);
        setIssuesMetadata(requestedIssuesMetadata);
    }, setIssuesMetadataLoading, setIssuesMetadataError), [issuesIDs, authFn, apiHostname, userEnterpriseId]);
    return {
        issuesMetadata,
        issuesMetadataLoading,
        issuesMetadataError,
        refetchIssuesMetadata
    };
};

const useSendToIntegrationModalLogic = (props) => {
    const [selectedIntegrationData, setSelectedIntegrationData] = React.useState();
    const [issues, setIssues] = React.useState([]);
    // Latest props/state are read through refs so the SendToIntegrationModal
    // component identity stays stable across renders. If it changed identity,
    // the consumer's `<integrationProps.SendToIntegrationModal />` would
    // unmount/remount and lose user-entered modal state on every parent
    // re-render (e.g. window resize).
    const propsRef = React.useRef(props);
    propsRef.current = props;
    const open = React.useCallback((slug, issuesToSend) => {
        const targetIntegration = propsRef.current.integrationData.find(({ productSlug }) => productSlug === slug);
        setSelectedIntegrationData(targetIntegration);
        setIssues(issuesToSend);
    }, []);
    const close = React.useCallback(() => {
        setSelectedIntegrationData(undefined);
        setIssues([]);
        propsRef.current.onModalClose?.();
    }, []);
    const SendToIntegrationModal$1 = React.useCallback(() => {
        const latestProps = propsRef.current;
        if (!latestProps.integrationData ||
            !selectedIntegrationData ||
            !issues.length) {
            return null;
        }
        return (jsxRuntime.jsx(SendToIntegrationModal, { ...latestProps, selectedIntegrationData: selectedIntegrationData, issues: issues, closeFn: close, onSuccess: latestProps.onSendIssueSuccess, onError: latestProps.onSendIssueError }));
    }, [selectedIntegrationData, issues, close]);
    return {
        open,
        close,
        SendToIntegrationModal: SendToIntegrationModal$1
    };
};

const useAPI = (loadData, skipRequest = false) => {
    const [loading, setLoading] = React__default.default.useState(!skipRequest);
    const [error, setError] = React__default.default.useState(null);
    const [data, setData] = React__default.default.useState(null);
    const [cacheBuster, setCacheBuster] = React__default.default.useState(1);
    React.useEffect(() => {
        if (skipRequest) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            let result = null;
            let err = null;
            try {
                result = await loadData();
            }
            catch (e) {
                /* istanbul ignore next*/
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
                err = e;
            }
            // Avoid memory leaks/etc.
            if (cancelled) {
                return;
            }
            setData(result);
            setError(err);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [loadData, cacheBuster, skipRequest]);
    const refresh = React.useCallback(() => setCacheBuster(prevCache => prevCache + 1), []);
    return {
        loading,
        error,
        data,
        refresh
    };
};

// After Jira creates an issue, JQL search needs about 1 second
// before the new issue shows up in results. To handle this delay,
// we retry the fetch a few times. Each retry waits longer than the
// previous one (the wait is multiplied by `pollBackoffMultiplier`,
// up to `maxPollIntervalMs`) so we don't spam the API on the early
// attempts. Callers can override any of these defaults.
const DEFAULT_POLL_INTERVAL_MS = 250;
const DEFAULT_MAX_POLLS = 8;
const DEFAULT_POLL_BACKOFF_MULTIPLIER = 1.5;
const DEFAULT_MAX_POLL_INTERVAL_MS = 2000;
const useGetIssuesStatuses = ({ userEnterpriseId, connectionId, issuesFn, groupId, issueIDExtractorFn, authFn, apiHostname, skipRequest = false }) => {
    const [poll, setPoll] = React.useState(null);
    const getIssuesStatusesCallback = React.useCallback(async () => {
        const isQueryByGroupId = !!groupId;
        const isQueryByIssueIds = !!issuesFn && !!issueIDExtractorFn;
        if (!connectionId ||
            !userEnterpriseId ||
            (!isQueryByGroupId && !isQueryByIssueIds)) {
            return null;
        }
        const token = await authFn();
        const requestParams = {
            apiHostname,
            token,
            enterpriseId: userEnterpriseId,
            connectionId
        };
        if (isQueryByGroupId) {
            return getIssuesStatuses({
                ...requestParams,
                groupId
            });
        }
        if (!isQueryByIssueIds) {
            return null;
        }
        const issues = await issuesFn();
        const issueIds = issues.map(issue => issueIDExtractorFn(issue));
        return getIssuesStatuses({
            ...requestParams,
            issueIds
        });
    }, [
        issuesFn,
        issueIDExtractorFn,
        authFn,
        apiHostname,
        userEnterpriseId,
        connectionId,
        groupId
    ]);
    const { data: issuesStatuses, loading: issuesStatusesLoading, error: issuesStatusesError, refresh } = useAPI(getIssuesStatusesCallback, skipRequest);
    React.useEffect(() => {
        if (!poll) {
            return;
        }
        if (issuesStatusesError) {
            setPoll(null);
            return;
        }
        if (skipRequest) {
            return;
        }
        if (issuesStatusesLoading) {
            return;
        }
        const allIssuesStatusesRetrieved = poll.ids.every(id => !!issuesStatuses?.[id]);
        if (allIssuesStatusesRetrieved || poll.remaining <= 0) {
            setPoll(null);
            return;
        }
        const timer = setTimeout(() => {
            refresh();
            setPoll(currentPoll => {
                if (!currentPoll) {
                    return null;
                }
                const nextIntervalMs = Math.min(currentPoll.intervalMs * currentPoll.backoffMultiplier, currentPoll.maxIntervalMs);
                return {
                    ...currentPoll,
                    remaining: currentPoll.remaining - 1,
                    intervalMs: nextIntervalMs
                };
            });
        }, poll.intervalMs);
        return () => clearTimeout(timer);
    }, [
        poll,
        skipRequest,
        issuesStatuses,
        issuesStatusesLoading,
        issuesStatusesError,
        refresh
    ]);
    const getIssueStatusByIssueId = React.useCallback((issueId) => {
        return issuesStatuses?.[issueId]?.status.name;
    }, [issuesStatuses]);
    // Safe to call while `skipRequest` is true: polling waits and starts
    // when `skipRequest` becomes false. Stops on success, error, budget
    // exhaustion, or unmount.
    const refetchIssuesStatuses = React.useCallback(({ awaitIssueIds, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, maxPolls = DEFAULT_MAX_POLLS, pollBackoffMultiplier = DEFAULT_POLL_BACKOFF_MULTIPLIER, maxPollIntervalMs = DEFAULT_MAX_POLL_INTERVAL_MS } = {}) => {
        if (awaitIssueIds?.length) {
            // Polling effect drives the fetches; first attempt fires after
            // `pollIntervalMs` so it doesn't race the next timer.
            setPoll({
                ids: awaitIssueIds,
                remaining: maxPolls,
                intervalMs: pollIntervalMs,
                backoffMultiplier: pollBackoffMultiplier,
                maxIntervalMs: maxPollIntervalMs
            });
            return;
        }
        if (skipRequest) {
            return;
        }
        refresh();
    }, [refresh, skipRequest]);
    return {
        issuesStatuses,
        issuesStatusesLoading,
        issuesStatusesError,
        getIssueStatusByIssueId,
        refetchIssuesStatuses
    };
};

exports.JiraFieldMappers = exportedJiraFieldMappers;
exports.JiraFieldMappersKeys = JiraFieldMappersToExportKeys;
exports.SendToIntegrationContainer = SendToIntegrationContainer;
exports.SendToIntegrationModal = SendToIntegrationModal;
exports.i18n = instance;
exports.supportedLocales = supportedLocales;
exports.useGetIssuesMetadata = useGetIssuesMetadata;
exports.useGetIssuesStatuses = useGetIssuesStatuses;
exports.useIntegrationsConfiguration = useIntegrationsConfiguration;
exports.useSendToIntegrationLogic = useSendToIntegrationLogic;
exports.useSendToIntegrationModalLogic = useSendToIntegrationModalLogic;
exports.useTranslation = useTranslation;
